import { availableAuditEntriesAtom, userDataAtom } from '@/global-store';
import LoadingButton from '@components/Button/LoadingButton';
import { useRescanAuditEntries } from '@http/queries/useRescanAuditEntries';
import { Tooltip, useNotification } from '@eye-able/ui';
import type { AuditData } from '@eye-able/types';
import { IconRadar } from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import type { PolicyResponse } from '@http/queries/useFetchPolicies';

type RescannableAuditEntry = Pick<
  AuditData,
  'id' | 'domain' | 'maxRescans' | 'rescanCounter'
>;

export function getRemainingRescans(entry: RescannableAuditEntry): number {
  return Math.max(0, (entry.maxRescans ?? 0) - (entry.rescanCounter ?? 0));
}

export function getPolicyRescannableAuditEntryIds(
  policy: Pick<PolicyResponse, 'domainScope' | 'auditEntryIds'>,
  auditEntries: RescannableAuditEntry[],
  isPrivilegedUser: boolean,
): number[] {
  const applicableAuditEntryIds =
    policy.domainScope === 'all-domains' ? null : new Set(policy.auditEntryIds);

  return auditEntries
    .filter(
      (entry) =>
        (applicableAuditEntryIds === null ||
          applicableAuditEntryIds.has(entry.id)) &&
        (isPrivilegedUser || getRemainingRescans(entry) > 0),
    )
    .map((entry) => entry.id);
}

export function isPolicyRescanDisabled(
  policy: Pick<PolicyResponse, 'domainScope' | 'status'>,
  auditEntryIds: number[],
  isPrivilegedUser: boolean,
): boolean {
  return (
    policy.status !== 'active' ||
    auditEntryIds.length === 0 ||
    (policy.domainScope === 'all-domains' && isPrivilegedUser)
  );
}

export function getPolicyRescanDisabledTooltipKey(
  policy: Pick<PolicyResponse, 'domainScope' | 'status'>,
  applicableAuditEntryIds: number[],
  rescannableAuditEntryIds: number[],
  isPrivilegedUser: boolean,
): string | undefined {
  if (policy.status !== 'active') {
    return 'governanceInactivePolicyRescanTooltip';
  }
  if (policy.domainScope === 'all-domains' && isPrivilegedUser) {
    return 'governanceAllDomainsAdminRescanTooltip';
  }
  if (applicableAuditEntryIds.length === 0) {
    return 'governanceNoDomainsToRescanTooltip';
  }
  if (rescannableAuditEntryIds.length === 0) {
    return 'governanceRescanLimitReachedTooltip';
  }

  return undefined;
}

export function usePolicyRescan() {
  const { t } = useInlineTranslation();
  const notification = useNotification();
  const auditEntries = useAtomValue(availableAuditEntriesAtom);
  const user = useAtomValue(userDataAtom);
  const rescanAuditEntries = useRescanAuditEntries();
  const isPrivilegedUser =
    user?.isAdmin === true || user?.isSalesAdmin === true;

  const getAuditEntryIds = useCallback(
    (policy: PolicyResponse) =>
      getPolicyRescannableAuditEntryIds(policy, auditEntries, isPrivilegedUser),
    [auditEntries, isPrivilegedUser],
  );
  const getApplicableAuditEntryIds = useCallback(
    (policy: PolicyResponse) =>
      getPolicyRescannableAuditEntryIds(policy, auditEntries, true),
    [auditEntries],
  );

  const rescanPolicy = useCallback(
    async (
      policy: PolicyResponse,
      {
        showSuccessNotification = true,
      }: { showSuccessNotification?: boolean } = {},
    ) => {
      const auditEntryIds = getAuditEntryIds(policy);

      if (isPolicyRescanDisabled(policy, auditEntryIds, isPrivilegedUser)) {
        return;
      }

      await rescanAuditEntries.mutateAsync({ auditEntryIds });
      if (showSuccessNotification) {
        notification.success(t('checkScheduleSuccess'));
      }
    },
    [getAuditEntryIds, isPrivilegedUser, notification, rescanAuditEntries, t],
  );

  return {
    getApplicableAuditEntryIds,
    getAuditEntryIds,
    isPrivilegedUser,
    isRescanning: rescanAuditEntries.isPending,
    rescanPolicy,
  };
}

/** Schedules an immediate rescan for every domain in a policy's scope. */
export function PolicyRescanControl({ policy }: { policy: PolicyResponse }) {
  const { t } = useInlineTranslation();
  const {
    getApplicableAuditEntryIds,
    getAuditEntryIds,
    isPrivilegedUser,
    isRescanning,
    rescanPolicy,
  } = usePolicyRescan();
  const applicableAuditEntryIds = useMemo(
    () => getApplicableAuditEntryIds(policy),
    [getApplicableAuditEntryIds, policy],
  );
  const auditEntryIds = useMemo(
    () => getAuditEntryIds(policy),
    [getAuditEntryIds, policy],
  );
  const disabledTooltipKey = getPolicyRescanDisabledTooltipKey(
    policy,
    applicableAuditEntryIds,
    auditEntryIds,
    isPrivilegedUser,
  );
  const isDisabled = isPolicyRescanDisabled(
    policy,
    auditEntryIds,
    isPrivilegedUser,
  );

  return (
    <Tooltip content={disabledTooltipKey ? t(disabledTooltipKey) : undefined}>
      <span className='eye:inline-flex' tabIndex={isDisabled ? 0 : undefined}>
        <LoadingButton
          type='button'
          variant='yellow'
          size='sm'
          loading={isRescanning}
          disabled={isDisabled}
          onClick={() => void rescanPolicy(policy)}
        >
          <IconRadar aria-hidden />
          {t('checkAgainButton')}
        </LoadingButton>
      </span>
    </Tooltip>
  );
}
